import ExternalLink from "@/components/ExternalLink";
import {
  BodyText,
  CardTitle,
  EyebrowText,
} from "@/components/ui/Typography";
import { PERSONAL_HUB } from "@/components/courses/personalHubTokens";
import { BRAND_COLORS } from "@/lib/brandColors";
import { Ionicons } from "@expo/vector-icons";
import { Image } from "expo-image";
import { LinearGradient } from "expo-linear-gradient";
import React, { useState } from "react";
import { Platform, Pressable } from "react-native";
import { Button, SizableText, XStack, YStack } from "tamagui";

export type NoticeTone = "promo" | "join" | "upgrade" | "neutral";

type ToneStyle = {
  rail: string;
  surface: string;
  border: string;
  eyebrow: string;
  title: string;
  summary: string;
  ctaBg: string;
  ctaColor: string;
  ctaHover?: string;
  orb?: string;
  featured?: boolean;
  /** Featured background wash (left → right) */
  wash?: [string, string, string];
};

const FEATURED_ORANGE: ToneStyle = {
  rail: BRAND_COLORS.accent,
  surface: "#12100E",
  border: "rgba(255,255,255,0.08)",
  eyebrow: BRAND_COLORS.logoOrange,
  title: "#FFFFFF",
  summary: "rgba(255,255,255,0.68)",
  ctaBg: BRAND_COLORS.accent,
  ctaColor: "#FFFFFF",
  ctaHover: "#D24B24",
  orb: "rgba(228, 87, 46, 0.22)",
  featured: true,
  wash: ["#241810", "#14110F", "#101418"],
};

const TONE: Record<NoticeTone, ToneStyle> = {
  promo: FEATURED_ORANGE,
  join: FEATURED_ORANGE,
  upgrade: {
    rail: BRAND_COLORS.logoBlue,
    surface: "#12100E",
    border: "rgba(255,255,255,0.08)",
    eyebrow: BRAND_COLORS.logoBlue,
    title: "#FFFFFF",
    summary: "rgba(255,255,255,0.68)",
    ctaBg: BRAND_COLORS.logoBlue,
    ctaColor: "#FFFFFF",
    ctaHover: "#1A9AC8",
    orb: "rgba(36, 171, 226, 0.28)",
    featured: true,
    wash: ["#14181C", "#12100E", "#0C1620"],
  },
  neutral: {
    rail: BRAND_COLORS.instructorMeta,
    surface: BRAND_COLORS.pageBg,
    border: BRAND_COLORS.border,
    eyebrow: BRAND_COLORS.instructorMeta,
    title: BRAND_COLORS.text,
    summary: BRAND_COLORS.muted,
    ctaBg: BRAND_COLORS.text,
    ctaColor: "#FFFFFF",
  },
};

export type AppNoticeBannerProps = {
  tone?: NoticeTone;
  eyebrow?: string;
  title: string;
  summary?: string | null;
  ctaLabel?: string | null;
  href?: string | null;
  onPress?: () => void;
  withSpacing?: boolean;
  /** Optional hub image; null/empty → text-only. */
  imageUrl?: string | null;
};

/** Hub notice / CTA banner — promo, join, upgrade. */
export function AppNoticeBanner({
  tone = "neutral",
  eyebrow,
  title,
  summary,
  ctaLabel,
  href,
  onPress,
  withSpacing = true,
  imageUrl,
}: AppNoticeBannerProps) {
  const [hovered, setHovered] = useState(false);
  const t = TONE[tone];
  const label = ctaLabel?.trim() || null;
  const url = href?.trim() || null;
  const thumb = imageUrl?.trim() || null;
  const interactive = Boolean(url || onPress);
  const featured = Boolean(t.featured);

  const card = (
    <XStack
      w="100%"
      overflow="hidden"
      borderRadius={featured ? PERSONAL_HUB.panelRadius : 14}
      borderWidth={1}
      borderColor={
        featured
          ? hovered
            ? tone === "upgrade"
              ? "rgba(36, 171, 226, 0.35)"
              : "rgba(228, 87, 46, 0.35)"
            : t.border
          : t.border
      }
      backgroundColor={t.surface}
      position="relative"
      {...(Platform.OS === "web"
        ? ({
            cursor: interactive ? "pointer" : "default",
            onMouseEnter: () => setHovered(true),
            onMouseLeave: () => setHovered(false),
            transition:
              "box-shadow 200ms ease, border-color 200ms ease",
            boxShadow: featured
              ? hovered && interactive
                ? "0 6px 28px rgba(0,0,0,0.18), 0 1px 2px rgba(0,0,0,0.08)"
                : PERSONAL_HUB.panelShadow
              : hovered && interactive
                ? "0 6px 18px rgba(13, 13, 13, 0.08)"
                : PERSONAL_HUB.panelShadow,
          } as object)
        : featured
          ? {
              shadowColor: "#0D0D0D",
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.12,
              shadowRadius: 12,
              elevation: 3,
            }
          : {})}
    >
      {featured ? (
        <>
          <LinearGradient
            colors={t.wash ?? ["#1A1614", "#12100E", "#0E1418"]}
            locations={[0, 0.5, 1]}
            start={{ x: 0, y: 0.5 }}
            end={{ x: 1, y: 0.5 }}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
            }}
            pointerEvents="none"
          />
          {/* Soft vertical depth */}
          <LinearGradient
            colors={["rgba(255,255,255,0.06)", "transparent", "rgba(0,0,0,0.28)"]}
            locations={[0, 0.4, 1]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
            }}
            pointerEvents="none"
          />
          {/* Brand glow — right */}
          <YStack
            position="absolute"
            right={-48}
            top="50%"
            marginTop={-90}
            width={200}
            height={200}
            borderRadius={999}
            backgroundColor={t.orb ?? "rgba(228, 87, 46, 0.2)"}
            opacity={hovered ? 0.95 : 0.7}
            {...(Platform.OS === "web"
              ? ({
                  transition: "opacity 240ms ease",
                  filter: "blur(28px)",
                } as object)
              : {})}
            pointerEvents="none"
          />
          {/* Secondary wash — left edge */}
          <YStack
            position="absolute"
            left={-40}
            bottom={-40}
            width={120}
            height={120}
            borderRadius={999}
            backgroundColor={
              tone === "upgrade"
                ? "rgba(36, 171, 226, 0.1)"
                : "rgba(228, 87, 46, 0.12)"
            }
            opacity={hovered ? 0.85 : 0.55}
            {...(Platform.OS === "web"
              ? ({
                  transition: "opacity 240ms ease",
                  filter: "blur(22px)",
                } as object)
              : {})}
            pointerEvents="none"
          />
          {/* Top glass edge */}
          <LinearGradient
            colors={["rgba(255,255,255,0.16)", "rgba(255,255,255,0)"]}
            start={{ x: 0.5, y: 0 }}
            end={{ x: 0.5, y: 1 }}
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: 1,
            }}
            pointerEvents="none"
          />
        </>
      ) : (
        <YStack
          w={4}
          minWidth={4}
          backgroundColor={t.rail}
          alignSelf="stretch"
        />
      )}

      <XStack
        f={1}
        ai="center"
        jc="space-between"
        gap="$3"
        flexWrap="wrap"
        px={featured ? 20 : 16}
        py={featured ? 16 : 14}
        zIndex={1}
      >
        <XStack f={1} minWidth={200} ai="center" gap={14} pr="$2">
          {thumb ? (
            <YStack
              width={64}
              height={64}
              borderRadius={PERSONAL_HUB.tileRadius}
              overflow="hidden"
              flexShrink={0}
              backgroundColor={
                featured ? "rgba(255,255,255,0.08)" : BRAND_COLORS.pageBg
              }
            >
              <Image
                source={{ uri: thumb }}
                style={{ width: "100%", height: "100%" }}
                contentFit="cover"
                transition={160}
                accessibilityIgnoresInvertColors
              />
            </YStack>
          ) : null}
          <YStack f={1} gap={6} minWidth={0}>
            {eyebrow ? (
              <EyebrowText
                color={t.eyebrow}
                textTransform="uppercase"
                letterSpacing={0.8}
              >
                {eyebrow}
              </EyebrowText>
            ) : null}
            <CardTitle color={t.title} fontWeight="600" letterSpacing={-0.15}>
              {title}
            </CardTitle>
            {summary ? (
              <BodyText color={t.summary} letterSpacing={0}>
                {summary}
              </BodyText>
            ) : null}
          </YStack>
        </XStack>

        {label ? (
          <Button
            bg={t.ctaBg}
            color={t.ctaColor}
            borderRadius={20}
            height={36}
            px={16}
            fontWeight="700"
            pressStyle={{ opacity: 0.92, scale: 0.99 }}
            hoverStyle={
              featured && t.ctaHover ? { bg: t.ctaHover } : undefined
            }
            pointerEvents="none"
            {...(featured && Platform.OS === "web"
              ? ({
                  boxShadow: hovered
                    ? tone === "upgrade"
                      ? "0 4px 12px rgba(36, 171, 226, 0.32)"
                      : "0 4px 12px rgba(228, 87, 46, 0.32)"
                    : tone === "upgrade"
                      ? "0 2px 8px rgba(36, 171, 226, 0.22)"
                      : "0 2px 8px rgba(228, 87, 46, 0.22)",
                  transition: "box-shadow 200ms ease, background 160ms ease",
                } as object)
              : {})}
          >
            <XStack ai="center" gap={6}>
              <SizableText
                color={t.ctaColor}
                fontSize={13}
                fontWeight="700"
                lineHeight={16}
              >
                {label}
              </SizableText>
              {featured ? (
                <Ionicons
                  name="arrow-forward"
                  size={15}
                  color={t.ctaColor}
                />
              ) : null}
            </XStack>
          </Button>
        ) : null}
      </XStack>
    </XStack>
  );

  const wrapped = withSpacing ? (
    <YStack w="100%" mb="$3">
      {card}
    </YStack>
  ) : (
    card
  );

  if (url) {
    return (
      <ExternalLink
        url={url}
        onPress={onPress}
        deferNavigation={Boolean(onPress)}
      >
        {wrapped}
      </ExternalLink>
    );
  }

  if (onPress) {
    return (
      <Pressable onPress={onPress} accessibilityRole="button">
        {wrapped}
      </Pressable>
    );
  }

  return wrapped;
}
